<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <h1 class="text-[22px] sm:text-[24px] font-bold text-gray-900 mb-6">
      Procurement
    </h1>

    <div v-if="!can.view" class="mt-10"></div>

    <template v-else>
      <div class="mb-6 max-w-xl">
        <label class="block text-sm font-semibold text-gray-800 mb-2">Company</label>
        <select
          v-model="selectedCompany"
          class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm"
        >
          <option disabled value="">Select company</option>
          <option v-for="c in companies" :key="c.id" :value="c.id">
            {{ c.name }}
          </option>
        </select>
      </div>

      <div v-if="selectedCompany" class="bg-white border rounded-2xl shadow-sm overflow-hidden">
        <DynamicTable
          :rows="rows"
          :columns="columns"
          :rows-per-page="50"
          search-placeholder="Search"
        >
          <template #header-actions>
            <button
              v-if="can.create"
              class="h-9 px-4 rounded-full text-white font-bold text-sm"
              :style="{ backgroundColor: '#5B9AC5' }"
              @click="goAdd"
            >
              + New Procurement
            </button>
          </template>

          <!-- Created By (read-only) -->
          <template #created_by="{ row }">
            <span class="text-gray-700 text-xs">{{ row.createdBy || '—' }}</span>
          </template>

          <!-- Approved By → dots (open modal) or dashes -->
          <template #approved_by="{ row }">
            <div class="flex items-center pl-2">
              <button
                v-if="row.approvers && row.approvers.length"
                class="text-gray-500 text-lg leading-none"
                @click="openApprovers(row)"
                aria-label="View approvers"
                title="View approvers"
              >⋮</button>
              <span v-else class="text-gray-400 select-none">-- --</span>
            </div>
          </template>

          <template #action="{ row }">
            <div class="flex items-center gap-3 text-sm">
              <!-- Approve (only if current user hasn't approved yet) -->
              <button
                v-if="can.approve && !row.approvedByMe"
                class="inline-flex items-center justify-center px-3 h-7 rounded-md bg-green-500 text-white font-medium hover:bg-green-600"
                @click="approveRow(row)"
              >
                Approve
              </button>

              <button
                v-if="can.viewProcurement"
                class="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900"
                @click="goView(row)"
              >
                <i class="far fa-eye text-xs mt-1"></i>
                <span>View</span>
              </button>

              <button
                v-if="can.edit"
                class="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900"
                @click="goEdit(row)"
              >
                <i class="fas fa-pen text-xs mt-1"></i>
                <span>Edit</span>
              </button>

              <span v-if="!can.viewProcurement && !can.edit" class="text-gray-400">—</span>
            </div>
          </template>
        </DynamicTable>
      </div>

      <!-- Approvers modal (read-only list of who approved) -->
      <transition name="fade">
        <div
          v-if="approversModal.open"
          class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          @click.self="closeApprovers"
        >
          <div class="relative bg-white w-full max-w-sm rounded-2xl p-6">
            <button
              class="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-lg"
              @click="closeApprovers"
            >✕</button>

            <div class="space-y-6 pt-2">
              <p v-if="!approversModal.names.length" class="text-center text-gray-400">
                No approvers yet.
              </p>

              <div
                v-for="n in approversModal.names"
                :key="n"
                class="text-center text-gray-800 font-semibold text-lg select-none"
              >
                {{ n }}
              </div>
            </div>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import api from '@/api'
import DynamicTable from '@/components/DynamicTable.vue'
import Swal from 'sweetalert2'

export default {
  components: { DynamicTable },

  data() {
    return {
      can: {
        view:            false,
        create:          false,
        viewProcurement: false,
        edit:            false,
        approve:         false,
        allowedCompanies: null,
      },

      selectedCompany: '',
      companies: [],
      rows: [],

      approversModal: {
        open: false,
        names: [],
      },

      columns: [
        { key: 'company_sequence', label: 'ID',             width: '60px'  },
        { key: 'invoice_no',       label: 'Invoice No',     width: '100px' },
        { key: 'supplier_name',    label: 'Supplier Name',  width: '230px' },
        { key: 'vehicle_no',       label: 'Vehicle Number', width: '110px' },
        { key: 'invoice_date',     label: 'Invoice Date',   width: '100px' },
        { key: 'amount',           label: 'Invoice Amount', width: '90px', align: 'center' },
        { key: 'created_by',       label: 'Created By',     width: '180px' },
        { key: 'approved_by',      label: 'Approved By',    width: '180px' },
        { key: 'action',           label: 'Action',         width: '220px' },
      ],
    }
  },

  computed: {
    authConfig() {
      const token = localStorage.getItem('token')
      return {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    },
  },

  watch: {
    selectedCompany(val) {
      if (val) {
        sessionStorage.setItem('procurement_selected_company_id', String(val))
        const compObj = this.companies.find(c => String(c.id) === String(val))
        if (compObj) {
          sessionStorage.setItem('procurement_selected_company_name', compObj.name)
        }
        this.fetchRows()
      } else {
        sessionStorage.removeItem('procurement_selected_company_id')
        sessionStorage.removeItem('procurement_selected_company_name')
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    if (to.name !== 'ProcurementForm') {
      sessionStorage.removeItem('procurement_selected_company_id')
      sessionStorage.removeItem('procurement_selected_company_name')
    }
    next()
  },

  async created() {
    await this.ensurePermissions()
    if (!this.can.view) return

    await this.fetchCompanies()

    const savedCompanyId = sessionStorage.getItem('procurement_selected_company_id')
    if (savedCompanyId) {
      const exists = this.companies.some(c => String(c.id) === String(savedCompanyId))
      if (exists) {
        this.selectedCompany = savedCompanyId
      }
    }
  },

  methods: {
    async ensurePermissions() {
      try {
        const res       = await api.get('/accounting/permissions/nav', this.authConfig)
        const nav       = res.data?.permissions || res.data || {}
        const pfSection = nav?.['Procurement Management']?.['Procurement'] || {}
        const granted   = pfSection.granted || []

        const has = (perm) => granted.includes(perm)

        this.can.view            = has('procurement_management.procurement.view')
        this.can.create          = has('procurement_management.procurement.new_procurement.create')
        this.can.viewProcurement = has('procurement_management.procurement.procurement.view')
        this.can.edit            = has('procurement_management.procurement.procurement.edit')
        this.can.approve         = has('procurement_management.procurement.procurement.approved')

        const prefix = 'procurement_management.procurement.company.'
        const suffix = '.view'
        const pfAll   = pfSection.all || []

        const allCompanyPerms     = pfAll.filter(p => p.startsWith(prefix) && p.endsWith(suffix))
        const grantedCompanyPerms = granted.filter(p => p.startsWith(prefix) && p.endsWith(suffix))

        if (allCompanyPerms.length === 0) {
          this.can.allowedCompanies = null
        } else {
          this.can.allowedCompanies = grantedCompanyPerms
            .map(p => p.slice(prefix.length, -suffix.length))
        }

      } catch (err) {
        console.error('[Procurement] ensurePermissions failed:', err)
        this.can = {
          view: false, create: false, viewProcurement: false,
          edit: false, approve: false, allowedCompanies: null,
        }
      }
    },

    async fetchCompanies() {
      try {
        const res  = await api.get('/accounting/companies', this.authConfig)
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || [])
        let companies = data.map(c => ({ id: c.id, name: c.name }))

        if (this.can.allowedCompanies !== null) {
          companies = companies.filter(c => this.can.allowedCompanies.includes(c.name))
        }

        this.companies = companies

        const savedCompanyId = sessionStorage.getItem('procurement_selected_company_id')
        if (savedCompanyId) {
          const compObj = this.companies.find(c => String(c.id) === String(savedCompanyId))
          if (compObj) {
            sessionStorage.setItem('procurement_selected_company_name', compObj.name)
          }
        }
      } catch (e) {
        console.error(e)
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const mm   = String(date.getMonth() + 1).padStart(2, '0')
      const dd   = String(date.getDate()).padStart(2, '0')
      const yyyy = date.getFullYear()
      return `${mm}/${dd}/${yyyy}`
    },

    async fetchRows() {
      if (!this.can.viewProcurement) return
      try {
        const res  = await api.get('/accounting/procurements', {
          ...this.authConfig,
          params: { company_id: this.selectedCompany , per_page: 9999 },
        })
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || [])

        const sortedData = [...data].sort((a, b) => {
          return (b.company_sequence || 0) - (a.company_sequence || 0)
        })

        this.rows = sortedData.map((item) => {
          // approvers can come as approved_by[] / approvers[] / legacy single name
          const raw = item.approved_by ?? item.approvers ?? item.approved_by_name ?? []
          const approvers = Array.isArray(raw)
            ? raw.filter(Boolean)
            : (raw ? [raw] : [])

          // backend may send it as approved_me or approved_by_me
          const approvedMeRaw = item.approved_by_me ?? item.approved_me

          return {
            id:               item.id,
            company_sequence: item.company_sequence,
            invoice_no:       item.invoice_number,
            supplier_name:    item.supplier_name,
            vehicle_no:       item.vehicle_number,
            invoice_date:     this.formatDate(item.invoice_date),
            amount:           Number(item.total_amount)
              ? (Number(item.total_amount) % 1 === 0
                  ? Number(item.total_amount).toString()
                  : Number(item.total_amount).toFixed(2).replace(/\.?0+$/, ''))
              : '0',
            // who created the procurement (fallback to legacy waiting_by_name until backend adds created_by)
            createdBy:    item.created_by || item.created_by_name || item.waiting_by_name || '',
            // names shown in the "Approved By" modal
            approvers,
            // has the current logged-in user already approved?
            approvedByMe: approvedMeRaw != null ? !!approvedMeRaw : false,
          }
        })
      } catch (e) {
        console.error(e)
        this.rows = []
      }
    },

    // ===== Approvers modal =====
    openApprovers(row) {
      this.approversModal = { open: true, names: row.approvers || [] }
    },

    closeApprovers() {
      this.approversModal = { open: false, names: [] }
    },

    // ===== Approve action (current user approves the row) =====
    async approveRow(row) {
      if (!this.can.approve) return
      try {
        await api.post(
          `/accounting/procurements/${row.id}/approve`,
          {},
          this.authConfig
        )

        Swal.fire({
          icon: 'success',
          title: 'Approved',
          toast: true,
          position: 'top',
          timer: 2000,
          showConfirmButton: false,
        })

        // refetch so approvers list + approved_by_me come back correct
        await this.fetchRows()
      } catch (e) {
        Swal.fire({
          icon: 'error',
          title: `Error ${e?.response?.status || ''}`.trim(),
          text: e?.response?.data?.message || e?.message || 'Failed to approve',
          toast: true,
          position: 'top',
          timer: 2200,
          showConfirmButton: false,
        })
      }
    },

    goAdd() {
      if (!this.can.create) return
      const company = this.companies.find(c => String(c.id) === String(this.selectedCompany))
      this.$router.push({
        name: 'ProcurementForm',
        state: {
          procMode:        'add',
          procCompanyId:   String(this.selectedCompany),
          procCompanyName: company?.name || sessionStorage.getItem('procurement_selected_company_name') || '',
          _ts: Date.now(),
        },
      })
    },

    goEdit(row) {
      if (!this.can.edit) return
      this.$router.push({
        name: 'ProcurementForm',
        state: {
          procMode: 'edit',
          procId:   String(row.id),
          procSeq:  String(row.company_sequence),
        },
      })
    },

    goView(row) {
      if (!this.can.viewProcurement) return
      this.$router.push({
        name: 'ProcurementForm',
        state: {
          procMode: 'view',
          procId:   String(row.id),
          procSeq:  String(row.company_sequence),
        },
      })
    },
  },
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
